import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowLeftRight, Box, BrickWall, Calendar as CalendarIcon, Check, ChevronDown, Crosshair, Eye, History, Loader2,
  Mountain, MountainSnow, MoveVertical, Package, Pencil, PlayCircle, RotateCcw, Ruler, Shovel, Square, Trash2, Undo2, Info, UtilityPole,
} from 'lucide-react';
import { getLatestImage } from '../../api/lastImage.api';
import { getDailyImages } from '../../api/iframeConfig.api';
import { getRotationDegrees } from '../../../utils/cameraOrientation.js';
import { useObraTimeZone } from '../../../obras/hooks/useObraTimeZone';
import { DatePickerModal, useDailyImagePicker } from '../firstLastImageCompare/FirstLastImageCompare';
import { compareFraming } from '../firstLastImageCompare/frameAlign';
import MedirFoto from './MedirFoto';
import MedirTutorial from './MedirTutorial';
import {
  FORMAS, LENTE_REOLINK, TIPOS, alPiso, arribaPorRotacion, baldosasPiso, calibrar, calidad as calcCalidad, decimales, dibujoMedida, fmtMedida,
  fmtNum, guiaVertical, medir, medirConError, muestras, ordenarEsquinas, revisarPendiente, rotarEsquinas, tramoPiso,
} from './medirGeom';
import { borrarMedida, errorMedir, guardarCalibracion, guardarMedida, leerMediciones, refDeUrl, renombrarMedida } from './medir.api';

// Pestaña "Medir" del Inicio de la cámara: calibrar una vez por cámara y medir distancias, superficies y alturas
// sobre la foto (geometría en el navegador, ver medirGeom.js).
// Pensado para alguien sin conocimientos técnicos: un tutorial animado, un solo paso para calibrar (4 esquinas de
// algo del piso + cuánto mide), la curva del lente es automática (perfil fijo de las cámaras, LENTE_REOLINK) y la
// verificación es mirar unas baldosas de 1 m sobre el piso.

const COLOR = { largo: '#ef4444', ancho: '#2563eb', vertical: '#f59e0b', plomada: '#0891b2', medida: '#16a34a', vista: '#9333ea', pared: '#64748b' };
const EJEMPLOS_RECT = [
  { t: 'Contenedor', d: '6 × 2,4', a: 6.06, b: 2.44 },
  { t: 'Contenedor grande', d: '12 × 2,4', a: 12.19, b: 2.44 },
  { t: 'Placa fenólica', d: '2,44 × 1,22', a: 2.44, b: 1.22 },
];
const EJEMPLOS_ALTO = [
  { t: 'Puerta', v: 2.05 },
  { t: 'Baño químico', v: 2.3 },
  { t: 'Contenedor', v: 2.59 },
  { t: 'Persona', v: 1.7 },
];
const CLAVE_TUTORIAL = 'lisual-medir-tutorial-visto';

const numero = (s) => {
  const n = parseFloat(String(s ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const txtNum = (x) => (x == null ? '' : String(x).replace('.', ','));
const red = (p) => (p.length > 2 ? [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10, Math.round(p[2] * 10) / 10] : [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10]);
const fechaDeUrl = (url) => {
  const m = String(url || '').match(/_(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})\.jpe?g/i);
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}` : null;
};
export const fechaCorta = (s, timeZone) => {
  if (!s) return '';
  if (/Z$|[+-]\d{2}:\d{2}$/.test(String(s))) {
    // fecha del servidor (UTC): a la hora de la obra (la del navegador si no se conoce)
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timeZone || undefined }).replace(',', '');
  }
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return String(s);
  return `${m[3]}/${m[2]}/${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ''}`;
};
// la precisión en palabras
const precisionEnPalabras = (x) => {
  if (x == null || !Number.isFinite(x)) return null;
  const pct = `±${fmtNum(x, x < 10 ? 1 : 0)} %`;
  if (x <= 3) return { txt: 'muy buena', pct, color: 'text-green-700' };
  if (x <= 6) return { txt: 'buena', pct, color: 'text-green-700' };
  if (x <= 12) return { txt: 'aceptable', pct, color: 'text-amber-700' };
  return { txt: 'baja', pct, color: 'text-red-700' };
};
const leerVisto = () => { try { return window.localStorage.getItem(CLAVE_TUTORIAL) === '1'; } catch (e) { return true; } };
const marcarVisto = () => { try { window.localStorage.setItem(CLAVE_TUTORIAL, '1'); } catch (e) { /* sin storage */ } };
const areaPx = (pts) => Math.abs(pts.reduce((a, p, i) => { const q = pts[(i + 1) % pts.length]; return a + p[0] * q[1] - q[0] * p[1]; }, 0)) / 2;

const borradorVacio = () => ({
  rect: { pts: [], largo: '', ancho: '', anchoNoSe: false, exacta: false, girar: false },
  vertical: { base: null, punta: null, alto: '', exacta: false },
  plomadas: [], // cosas verticales sin alto (postes, esquinas): controlan que el piso marcado esté derecho
});

// calibración guardada -> entrada de calibrar(). Sin líneas marcadas a mano, la curva del lente es la del perfil.
const calDesdeGuardada = (c, rot) => {
  if (!c) return null;
  const conLineas = (c.lineas || []).some((l) => l.pts?.length >= 3);
  return {
    arriba: arribaPorRotacion(rot), imagen: { w: c.foto.w, h: c.foto.h }, rect: c.rect, lineas: c.lineas || [], verticales: c.verticales || [], segmentos: c.segmentos || [],
    ...(conLineas ? {} : { lente: LENTE_REOLINK }),
  };
};

// borrador -> calibraciones candidatas. El cliente toca las esquinas en cualquier orden y no dice qué lado es el
// largo: hay dos lecturas (largo en un par de lados o en el otro, `asig` 0/1) y, como el ajuste arranca distinto según
// la esquina de partida, cada lectura se prueba empezando por dos esquinas vecinas. Se queda la que mejor cierra.
const calsDesdeBorrador = (b, img, rot) => {
  const L = numero(b.rect.largo);
  const A = b.rect.anchoNoSe ? null : numero(b.rect.ancho);
  if (!img || b.rect.pts.length !== 4 || !(L > 0) || (!b.rect.anchoNoSe && !(A > 0))) return null;
  const alto = numero(b.vertical.alto);
  const pts = ordenarEsquinas(b.rect.pts);
  const base = {
    arriba: arribaPorRotacion(rot),
    imagen: { w: img.w, h: img.h },
    lineas: [],
    verticales: b.vertical.base && b.vertical.punta && alto > 0 ? [{ base: b.vertical.base, punta: b.vertical.punta, alto, exacta: !!b.vertical.exacta }] : [],
    segmentos: [],
    lente: LENTE_REOLINK,
  };
  const out = [];
  for (const asig of [0, 1]) {
    for (const k of [0, 1]) {
      const p = k ? rotarEsquinas(pts) : pts;
      // ¿el lado 1-2 de esta lectura es el largo?
      const largoPrimero = (asig === 0) === (k === 0);
      if (!largoPrimero && A == null) continue; // sin ancho, el lado 1-2 tiene que ser el que se conoce
      out.push({ asig, largoPrimero, cal: { ...base, rect: { pts: p, a: largoPrimero ? L : A, b: largoPrimero ? A : L, exacta: !!b.rect.exacta } } });
    }
  }
  if (A == null) {
    // sin ancho: también arrancando desde la esquina opuesta
    out.push({ asig: 0, largoPrimero: true, cal: { ...out[0].cal, rect: { ...out[0].cal.rect, pts: rotarEsquinas(rotarEsquinas(pts)) } } });
    out.push({ asig: 1, largoPrimero: true, cal: { ...out[1].cal, rect: { ...out[1].cal.rect, pts: rotarEsquinas(rotarEsquinas(rotarEsquinas(pts))) } } });
  }
  return out;
};

// Margen de error (Monte Carlo) fuera del render: tarda unas décimas.
function useVecinas(cal, ajuste) {
  const [v, setV] = useState({ lista: [], listo: false });
  useEffect(() => {
    if (!cal || !ajuste?.ok) { setV({ lista: [], listo: false }); return undefined; }
    setV({ lista: [], listo: false });
    const t = setTimeout(() => setV({ lista: muestras(cal, ajuste), listo: true }), 30);
    return () => clearTimeout(t);
  }, [cal, ajuste]);
  return v;
}

const Boton = ({ children, onClick, disabled, variante = 'sec', className = '', title, type = 'button', grande = false }) => (
  <button
    type={type}
    title={title}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-1.5 rounded-xl font-semibold transition-colors active:scale-95 disabled:opacity-40 disabled:pointer-events-none ${grande ? 'px-4 py-2.5 text-base' : 'px-3 py-2 text-sm'} ${
      variante === 'pri' ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm' : variante === 'ok' ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm' : variante === 'peligro' ? 'text-red-700 hover:bg-red-50 border border-red-200' : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50'
    } ${className}`}
  >
    {children}
  </button>
);

const CampoGrande = ({ label, value, onChange, color, id, deshabilitado, placeholder = '0,0', inputRef }) => (
  <label htmlFor={id} className={`flex-1 min-w-[130px] rounded-xl border-2 bg-white px-3 py-2 ${deshabilitado ? 'border-gray-100 opacity-60' : 'border-gray-200 focus-within:border-blue-500'}`}>
    <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
      {color && <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: color }} />}{label}
    </span>
    <span className="flex items-baseline gap-1">
      <input
        ref={inputRef}
        id={id}
        inputMode="decimal"
        value={value}
        disabled={deshabilitado}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full min-w-0 bg-transparent text-2xl font-bold text-gray-900 outline-none placeholder:text-gray-300"
      />
      <span className="text-base font-semibold text-gray-400">m</span>
    </span>
  </label>
);

const Aviso = ({ tono = 'amber', icon, children }) => {
  const Icon = icon || (tono === 'blue' || tono === 'green' ? Check : AlertTriangle);
  const t = { amber: 'text-amber-800 bg-amber-50 border-amber-200', red: 'text-red-700 bg-red-50 border-red-200', blue: 'text-blue-800 bg-blue-50 border-blue-200', green: 'text-green-800 bg-green-50 border-green-200', gray: 'text-gray-600 bg-gray-50 border-gray-200' }[tono];
  return (
    <div className={`flex items-start gap-1.5 text-sm border rounded-xl px-3 py-2 ${t}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
};

// Dibujo del paso principal: algo grande del piso, cerca de lo que se mide (✓) y algo chico y lejos (✗).
const DibujoRectangulo = () => (
  <svg viewBox="0 0 240 120" className="w-full h-auto" aria-hidden="true">
    <rect width="240" height="120" rx="10" fill="#f8fafc" />
    <path d="M0 38 L240 38 L240 120 L0 120 Z" fill="#e7dccb" />
    <rect x="18" y="16" width="34" height="22" fill="#cbd5e1" /><rect x="190" y="10" width="40" height="28" fill="#cbd5e1" />
    {/* chico y lejos */}
    <path d="M150 46 L170 46 L172 52 L149 52 Z" fill="#fca5a5" stroke="#ef4444" strokeWidth="1.5" />
    <g transform="translate(176 44)"><circle r="8" fill="#ef4444" /><path d="M-3 -3 L3 3 M3 -3 L-3 3" stroke="#fff" strokeWidth="2" /></g>
    {/* grande y cerca */}
    <path d="M40 72 L150 72 L170 108 L22 108 Z" fill="#bfdbfe" stroke="#2563eb" strokeWidth="2.5" />
    {[[40, 72], [150, 72], [170, 108], [22, 108]].map(([x, y], i) => (
      <g key={i}><circle cx={x} cy={y} r="6.5" fill="#fff" stroke="#2563eb" strokeWidth="2" /><text x={x} y={y + 3.2} textAnchor="middle" fontSize="8" fontWeight="700" fill="#1d4ed8">{i + 1}</text></g>
    ))}
    <g transform="translate(96 90)"><circle r="10" fill="#16a34a" /><path d="M-4.5 0 L-1 3.5 L5 -3.5" stroke="#fff" strokeWidth="2.4" fill="none" /></g>
  </svg>
);

// Dibujo del paso de altura: algo parado, tocar abajo (donde apoya en el piso) y arriba.
const DibujoAltura = () => (
  <svg viewBox="0 0 160 120" className="w-full h-auto" aria-hidden="true">
    <rect width="160" height="120" rx="10" fill="#f8fafc" />
    <path d="M0 92 L160 92 L160 120 L0 120 Z" fill="#e7dccb" />
    <rect x="54" y="20" width="40" height="72" rx="3" fill="#2563eb" /><rect x="54" y="20" width="40" height="10" rx="3" fill="#1d4ed8" />
    <line x1="74" y1="92" x2="74" y2="20" stroke="#f59e0b" strokeWidth="2.5" strokeDasharray="5 4" />
    <rect x="68" y="86" width="12" height="12" rx="2" fill="#fff" stroke="#f59e0b" strokeWidth="2.5" />
    <circle cx="74" cy="20" r="6.5" fill="#fff" stroke="#f59e0b" strokeWidth="2.5" />
    <text x="100" y="24" fontSize="11" fontWeight="700" fill="#92400e">2 · arriba</text>
    <text x="86" y="108" fontSize="11" fontWeight="700" fill="#92400e">1 · abajo</text>
  </svg>
);

// Ejemplo animado del paso "enderezar": un poste, se toca abajo y después arriba.
const DibujoPlomada = () => (
  <svg viewBox="0 0 160 120" className="w-full h-auto" aria-hidden="true">
    <rect width="160" height="120" rx="10" fill="#f8fafc" />
    <path d="M0 96 L160 96 L160 120 L0 120 Z" fill="#e7dccb" />
    <rect x="18" y="30" width="34" height="66" fill="#cbd5e1" /><rect x="18" y="30" width="34" height="66" fill="none" stroke="#94a3b8" />
    <rect x="96" y="14" width="5" height="82" fill="#64748b" /><rect x="88" y="14" width="22" height="4" fill="#64748b" />
    <line x1="98.5" y1="96" x2="98.5" y2="16" stroke="#0891b2" strokeWidth="3" strokeLinecap="round" strokeDasharray="80" strokeDashoffset="80">
      <animate attributeName="stroke-dashoffset" values="80;80;0;0;80" keyTimes="0;0.3;0.55;0.9;1" dur="3s" repeatCount="indefinite" />
    </line>
    <rect x="92.5" y="90" width="12" height="12" rx="2" fill="#fff" stroke="#0891b2" strokeWidth="2.5" opacity="0">
      <animate attributeName="opacity" values="0;1;1;1;0" keyTimes="0;0.15;0.5;0.9;1" dur="3s" repeatCount="indefinite" />
    </rect>
    <circle cx="98.5" cy="16" r="6" fill="#fff" stroke="#0891b2" strokeWidth="2.5" opacity="0">
      <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.45;0.55;0.9;1" dur="3s" repeatCount="indefinite" />
    </circle>
    <g>
      <path d="M0 0 L0 13 L3.5 9.5 L6 15 L8.5 14 L6 8.5 L11 8.5 Z" fill="#111827" stroke="#fff" strokeWidth="1">
        <animateMotion values="125,112;101,99;101,99;101,19;101,19;125,112" keyTimes="0;0.12;0.3;0.45;0.6;1" dur="3s" repeatCount="indefinite" />
      </path>
    </g>
    <text x="112" y="104" fontSize="10" fontWeight="700" fill="#0e7490">abajo</text>
    <text x="112" y="24" fontSize="10" fontWeight="700" fill="#0e7490">arriba</text>
  </svg>
);

// Cuando se toca el cielo (o más allá del horizonte): qué se puede medir desde una sola foto.
const AvisoCielo = ({ onCerrar }) => (
  <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-3">
    <svg viewBox="0 0 120 80" className="w-24 shrink-0" aria-hidden="true">
      <rect width="120" height="38" fill="#e0f2fe" /><rect y="38" width="120" height="42" fill="#e7dccb" />
      <line x1="0" y1="38" x2="120" y2="38" stroke="#94a3b8" strokeDasharray="3 3" />
      <ellipse cx="30" cy="16" rx="14" ry="6" fill="#fff" />
      <g transform="translate(78 14)"><circle r="7" fill="#ef4444" /><path d="M-3 -3 L3 3 M3 -3 L-3 3" stroke="#fff" strokeWidth="2" /></g>
      <rect x="44" y="30" width="4" height="34" fill="#64748b" />
      <g transform="translate(46 30)"><circle r="6" fill="#16a34a" /><path d="M-3 0 L-1 2.5 L3 -2.5" stroke="#fff" strokeWidth="1.8" fill="none" /></g>
      <g transform="translate(80 62)"><circle r="6" fill="#16a34a" /><path d="M-3 0 L-1 2.5 L3 -2.5" stroke="#fff" strokeWidth="1.8" fill="none" /></g>
    </svg>
    <div className="text-sm text-sky-900 min-w-0">
      <p className="font-semibold">Ese punto está en el cielo o más allá del horizonte.</p>
      <p>Desde una sola foto medimos sobre el piso que calibraste y en cosas apoyadas en él. Para algo alto, usá «Altura» o «En pared», o tocá algo que esté apoyado en el piso.</p>
      {onCerrar && <button type="button" onClick={onCerrar} className="mt-1 font-semibold underline">Entendido</button>}
    </div>
  </div>
);

const Limites = () => {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button type="button" onClick={() => setAbierto((x) => !x)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 text-left">
        <Info className="w-4 h-4 text-blue-600" /> ¿Qué tan exacto es?
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${abierto ? 'rotate-180' : ''}`} />
      </button>
      {abierto && (
        <ul className="px-4 pb-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
          <li>Desde una sola foto medimos <b>exacto sobre el piso que calibraste y en cosas apoyadas en él</b>. Si algo está en altura, marcá también dónde se apoya en el piso; para fachadas usá «En pared».</li>
          <li>El <b>volumen</b> es una estimación: suponemos una forma (montaña, caja, pozo) y lo que no se ve en la foto no se cuenta.</li>
          <li>Cerca de lo que marcaste mide mejor; <b>lejos de la cámara</b> el ± crece.</li>
          <li>Para las <b>alturas</b>, tocá primero donde la cosa apoya en el piso.</li>
          <li>Hacé <b>zoom</b> (rueda o pellizco) para tocar justo en el lugar.</li>
          <li>Si <b>la cámara se mueve</b>, hay que volver a calibrar (te avisamos).</li>
        </ul>
      )}
    </div>
  );
};

const PasosChips = ({ pasos, actual }) => (
  <div className="flex items-center gap-1.5" aria-label={`Paso ${actual + 1} de ${pasos.length}`}>
    {pasos.map((p, i) => (
      <span key={p} className={`flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${i === actual ? 'bg-blue-600 text-white' : i < actual ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
        {i < actual ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}<span className="hidden sm:inline">{p}</span>
      </span>
    ))}
  </div>
);

const MedirObra = ({ user }) => {
  const feedId = user?.feedID;
  const obraTimeZone = useObraTimeZone(feedId);
  const fechaObra = (s) => fechaCorta(s, obraTimeZone);
  const rot = getRotationDegrees(user?.orientation) || 0;
  const rotacion = rot === 360 ? 0 : rot;

  const [datos, setDatos] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [foto, setFoto] = useState(null);
  const [img, setImg] = useState(null);
  const [fotoError, setFotoError] = useState(null);
  const picker = useDailyImagePicker(feedId);

  const [modo, setModo] = useState('cargando'); // cargando | intro | calibrar | medir
  const [paso, setPaso] = useState('esquinas'); // esquinas | alto | ver
  const [conAlto, setConAlto] = useState(false); // el paso de algo parado (obligatorio si no sabe el ancho)
  const [borrador, setBorrador] = useState(borradorVacio);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [tutorial, setTutorial] = useState(null); // null | { congelar }
  const largoRef = useRef(null);

  const [herramienta, setHerramienta] = useState('distancia');
  const [pts, setPts] = useState([]);
  const [bases, setBases] = useState([]); // distancia: dónde se apoya en el piso cada punto que está en altura (o null)
  const [esperaBase, setEsperaBase] = useState(null); // índice del punto al que le falta dónde se apoya
  const [linea, setLinea] = useState([]); // en pared: los 2 puntos del pie de la pared
  const [cima, setCima] = useState(null); // volumen: la punta (o el fondo del pozo)
  const [forma, setForma] = useState('pila');
  const [faseVol, setFaseVol] = useState('base'); // base | cima
  const [cursorP, setCursorP] = useState(null); // dónde está el mouse (vista previa en vivo)
  const [cielo, setCielo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [repetir, setRepetir] = useState(null);
  const [vista, setVista] = useState(null); // medida guardada que se está mirando
  const [renombrando, setRenombrando] = useState(null);
  const [abiertas, setAbiertas] = useState({});
  const [encuadre, setEncuadre] = useState(null);

  // ---- carga ----
  useEffect(() => {
    if (!feedId) return undefined;
    let vivo = true;
    leerMediciones(feedId)
      .then((d) => { if (vivo) { setDatos(d); setModo(d.calibracion ? 'medir' : 'intro'); } })
      .catch((e) => { if (vivo) { setErrorCarga(errorMedir(e)); setModo('intro'); } });
    // Sólo local (/__preview-inicio?medirFoto=YYYY-MM-DDTHH): arrancar en una foto puntual para las pruebas.
    const enPreview = process.env.NODE_ENV === 'development' && window.location.pathname === '/__preview-inicio';
    const qs = enPreview ? new URLSearchParams(window.location.search) : null;
    const pedida = qs?.get('medirFoto');
    // Sólo local: ?medirTutorial=7.5 abre el tutorial congelado en ese segundo (capturas).
    if (qs?.has('medirTutorial')) setTutorial({ congelar: Number(qs.get('medirTutorial')) || null });
    const primera = pedida
      ? getDailyImages(feedId, pedida.slice(0, 10)).then((l) => (Array.isArray(l) ? l : []).find((x) => String(x?.date || '').startsWith(pedida)) || getLatestImage(feedId))
      : getLatestImage(feedId);
    primera
      .then((r) => {
        if (!vivo) return;
        const url = typeof r === 'object' ? r?.url : r;
        if (!url) { setFotoError('Todavía no hay fotos de esta cámara.'); return; }
        setFoto({ url, date: (typeof r === 'object' && r?.date) || fechaDeUrl(url) });
      })
      .catch(() => vivo && setFotoError('No pudimos traer la última foto.'));
    return () => { vivo = false; };
  }, [feedId]);

  // la primera vez que alguien entra sin calibración, el tutorial arranca solo
  useEffect(() => {
    if (modo === 'intro' && !tutorial && !leerVisto()) { marcarVisto(); setTutorial({ congelar: null }); }
  }, [modo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!foto?.url) return undefined;
    let vivo = true;
    setImg(null);
    const im = new Image();
    im.onload = () => vivo && setImg({ w: im.naturalWidth, h: im.naturalHeight, url: foto.url });
    im.onerror = () => vivo && setFotoError('No se pudo cargar la foto.');
    im.src = foto.url;
    return () => { vivo = false; };
  }, [foto]);

  // ---- calibración guardada ----
  const calibracion = datos?.calibracion || null;
  const calG = useMemo(() => calDesdeGuardada(calibracion, rotacion), [calibracion, rotacion]);
  const ajusteG = useMemo(() => (calG ? calibrar(calG) : null), [calG]);
  const vecG = useVecinas(calG, ajusteG);
  const mismaFoto = calibracion && foto && refDeUrl(foto.url) === calibracion.foto.ref;
  // misma relación de aspecto, otra resolución: se escalan los puntos
  const escala = calibracion && img ? calibracion.foto.w / img.w : 1;
  const aspectoDistinto = calibracion && img && Math.abs(calibracion.foto.w / calibracion.foto.h - img.w / img.h) > 0.01;

  // ¿la cámara se movió desde la calibración?
  useEffect(() => {
    if (!calibracion || !foto || !img || modo !== 'medir') return undefined;
    if (mismaFoto) { setEncuadre({ estado: 'same' }); return undefined; }
    if (aspectoDistinto) { setEncuadre({ estado: 'moved' }); return undefined; }
    let vivo = true;
    setEncuadre({ estado: 'verificando' });
    (async () => {
      try {
        const dia = String(calibracion.foto.fecha).slice(0, 10);
        const lista = await getDailyImages(feedId, dia);
        const items = (Array.isArray(lista) ? lista : []).map((x) => (typeof x === 'string' ? { url: x } : x)).filter((x) => x?.url);
        const misma = items.find((x) => refDeUrl(x.url) === calibracion.foto.ref);
        if (!misma) { if (vivo) setEncuadre({ estado: 'sin-dato' }); return; }
        const r = await compareFraming(foto.url, misma.url);
        if (vivo) setEncuadre({ estado: r.status });
      } catch (e) {
        if (vivo) setEncuadre({ estado: 'sin-dato' });
      }
    })();
    return () => { vivo = false; };
  }, [calibracion, foto, img, modo, mismaFoto, aspectoDistinto, feedId]);

  // ---- borrador de calibración ----
  const cals = useMemo(() => (modo === 'calibrar' ? calsDesdeBorrador(borrador, img, rotacion) : null), [modo, borrador, img, rotacion]);
  const ajustes = useMemo(() => (cals ? cals.map((c) => calibrar(c.cal)) : null), [cals]);
  // la mejor de cada lectura; el largo va donde cierra mejor (o en el otro par de lados, si el cliente lo da vuelta)
  const elegido = useMemo(() => {
    if (!ajustes) return null;
    const mejor = [0, 1].map((asig) => {
      let i = -1;
      ajustes.forEach((a, j) => { if (cals[j].asig === asig && a.ok && (i < 0 || a.costo < ajustes[i].costo)) i = j; });
      return i;
    });
    let asig = mejor[0] >= 0 && mejor[1] >= 0 ? (ajustes[mejor[0]].costo <= ajustes[mejor[1]].costo ? 0 : 1) : mejor[0] >= 0 ? 0 : mejor[1] >= 0 ? 1 : null;
    if (asig == null) return { i: 0, ambas: false };
    if (borrador.rect.girar && mejor[1 - asig] >= 0) asig = 1 - asig;
    return { i: mejor[asig], ambas: mejor[0] >= 0 && mejor[1] >= 0 };
  }, [ajustes, cals, borrador.rect.girar]);
  const calB = cals && elegido ? cals[elegido.i].cal : null;
  const ajusteB = ajustes && elegido ? ajustes[elegido.i] : null;
  const calBVerif = paso === 'ver' ? calB : null;
  const vecB = useVecinas(calBVerif, calBVerif ? ajusteB : null);
  const calidadB = useMemo(() => (paso === 'ver' && ajusteB?.ok && vecB.listo ? calcCalidad(calB, ajusteB, vecB.lista) : null), [paso, calB, ajusteB, vecB]);
  const plomadasOk = useMemo(() => borrador.plomadas.filter((x) => x.base && x.punta), [borrador.plomadas]);
  const pendiente = useMemo(() => (modo === 'calibrar' && ajusteB?.ok && plomadasOk.length && img ? revisarPendiente(ajusteB, plomadasOk, img.w) : null), [modo, ajusteB, plomadasOk, img]);
  const calidadG = useMemo(() => (ajusteG?.ok && vecG.listo ? calcCalidad(calG, ajusteG, vecG.lista) : null), [calG, ajusteG, vecG]);

  const empezarCalibracion = (prellenar) => {
    setMensaje(null);
    setVista(null);
    setPts([]);
    setRepetir(null);
    if (prellenar && calibracion && (mismaFoto || encuadre?.estado === 'same') && !aspectoDistinto) {
      const c = calibracion;
      const v = c.verticales?.[0];
      const s = 1 / escala;
      const sc = (p) => [p[0] * s, p[1] * s, p[2]];
      setBorrador({
        rect: { pts: c.rect.pts.map(sc), largo: txtNum(c.rect.a), ancho: c.rect.b ? txtNum(c.rect.b) : '', anchoNoSe: !c.rect.b, exacta: !!c.rect.exacta, girar: false },
        vertical: v ? { base: sc(v.base), punta: sc(v.punta), alto: txtNum(v.alto), exacta: v.exacta } : { base: null, punta: null, alto: '', exacta: false },
        plomadas: (c.plomadas || []).map((x) => ({ base: sc(x.base), punta: sc(x.punta) })),
      });
      setConAlto(!!v || !c.rect.b);
    } else {
      setBorrador(borradorVacio());
      setConAlto(false);
    }
    setPaso('esquinas');
    setModo('calibrar');
  };

  const agregarCalibracion = (p) => {
    setBorrador((b) => {
      if (paso === 'esquinas') {
        if (b.rect.pts.length >= 4) return b;
        return { ...b, rect: { ...b.rect, pts: [...b.rect.pts, p], girar: false } };
      }
      if (paso === 'alto') {
        const v = b.vertical;
        if (!v.base) return { ...b, vertical: { ...v, base: p } };
        if (!v.punta) return { ...b, vertical: { ...v, punta: p } };
        return { ...b, vertical: { ...v, base: p, punta: null } };
      }
      if (paso === 'plomadas') {
        const l = b.plomadas.slice();
        const u = l[l.length - 1];
        if (u && !u.punta) l[l.length - 1] = { ...u, punta: p };
        else if (l.length < 2) l.push({ base: p, punta: null });
        else return b;
        return { ...b, plomadas: l };
      }
      return b;
    });
  };

  const moverCalibracion = (ref, p) => {
    setBorrador((b) => {
      const [g, i] = ref;
      if (g === 'rect') { const pts2 = b.rect.pts.slice(); pts2[i] = p; return { ...b, rect: { ...b.rect, pts: pts2 } }; }
      if (g === 'base' || g === 'punta') return { ...b, vertical: { ...b.vertical, [g]: p } };
      if (g === 'pb' || g === 'pp') return { ...b, plomadas: b.plomadas.map((x, k) => (k === i ? { ...x, [g === 'pb' ? 'base' : 'punta']: p } : x)) };
      return b;
    });
  };

  const guardarCal = async () => {
    if (!ajusteB?.ok || !img) return;
    setGuardando(true);
    setMensaje(null);
    try {
      const m = ajusteB.modelo;
      const q = calidadB || {};
      const d = await guardarCalibracion(feedId, {
        foto: { fecha: foto.date || fechaDeUrl(foto.url) || '', ref: refDeUrl(foto.url), w: img.w, h: img.h },
        rect: { ...calB.rect, pts: calB.rect.pts.map(red) },
        lineas: [],
        verticales: calB.verticales.map((v) => ({ ...v, base: red(v.base), punta: red(v.punta) })),
        segmentos: [],
        plomadas: plomadasOk.map((x) => ({ base: red(x.base), punta: red(x.punta) })),
        control: null,
        calidad: { precisionPct: q.precisionPct, lejosPct: q.lejosPct, alturaPct: q.alturaPct, alturaCamara: q.alturaCamara, hfov: q.hfov, distorsion: q.distorsion, rms: q.rms },
        modelo: { f: m.f, k: m.k, rv: m.rv, t: m.t, signo: ajusteB.signo },
      });
      setDatos(d);
      setModo('medir');
      setEncuadre({ estado: 'same' });
      setMensaje({ tono: 'green', texto: '¡Listo! La cámara quedó calibrada. Ya podés medir en cualquier foto de esta cámara.' });
    } catch (e) {
      setMensaje({ tono: 'red', texto: errorMedir(e) });
    } finally {
      setGuardando(false);
    }
  };

  // ---- medir ----
  const camaraMovida = encuadre?.estado === 'moved';
  const aCal = useCallback((p) => [p[0] * escala, p[1] * escala, (p[2] || 0) * escala], [escala]);
  const aImg = useCallback((p) => [p[0] / escala, p[1] / escala], [escala]);
  const mG = ajusteG?.ok ? ajusteG.modelo : null;
  const ptsCal = useMemo(() => pts.map(aCal), [pts, aCal]);
  const extraCal = useMemo(() => ({
    bases: bases.map((x) => (x ? aCal(x) : null)), linea: linea.map(aCal), cima: cima ? aCal(cima) : null, forma,
  }), [bases, linea, cima, forma, aCal]);
  const completa = herramienta === 'volumen' ? pts.length >= 3 && !!cima
    : herramienta === 'pared' ? linea.length === 2 && pts.length >= 2
      : herramienta === 'distancia' ? pts.length >= 2 && esperaBase == null
        : pts.length >= TIPOS[herramienta].min;
  const resultado = useMemo(() => {
    if (modo !== 'medir' || !mG || !completa) return null;
    const r = medirConError(ajusteG, vecG.lista, herramienta, ptsCal, { extra: extraCal });
    // con un margen más grande que la medida no tiene sentido mostrar el número
    return r && vecG.listo && (r.inestable || r.errPct > 100) ? { ...r, fuera: true } : r;
  }, [modo, mG, completa, ajusteG, vecG, herramienta, ptsCal, extraCal]);

  // vista previa con el mouse: la línea y el número, antes de tocar
  const previa = useMemo(() => {
    if (modo !== 'medir' || !mG || !cursorP || camaraMovida || completa) return null;
    const cp = aCal(cursorP);
    let tipo = null; let P = null; let ex = extraCal;
    if (herramienta === 'distancia' && pts.length >= 1 && esperaBase == null) { tipo = 'distancia'; P = [...ptsCal, cp]; ex = { bases: [...extraCal.bases.slice(0, pts.length), null] }; }
    else if (herramienta === 'altura' && pts.length === 1) { tipo = 'altura'; P = [ptsCal[0], cp]; }
    else if (herramienta === 'area' && pts.length >= 2) { tipo = 'area'; P = [...ptsCal, cp]; }
    else if (herramienta === 'pared' && linea.length === 2 && pts.length >= 1) { tipo = 'pared'; P = [...ptsCal, cp]; }
    else if (herramienta === 'volumen' && faseVol === 'cima' && pts.length >= 3) { tipo = 'volumen'; P = ptsCal; ex = { ...extraCal, cima: cp }; }
    if (!tipo) return null;
    const valor = medir(mG, tipo, P, ex);
    const d = dibujoMedida(mG, tipo, tipo === 'distancia' || tipo === 'pared' ? P.slice(-2) : P, tipo === 'distancia' ? { bases: ex.bases.slice(-2) } : ex);
    return valor != null && Number.isFinite(valor) && d ? { tipo, valor, d } : null;
  }, [modo, mG, cursorP, camaraMovida, completa, aCal, herramienta, pts, ptsCal, esperaBase, extraCal, linea, faseVol]);

  const limpiar = () => { setPts([]); setBases([]); setEsperaBase(null); setLinea([]); setCima(null); setFaseVol('base'); setCielo(false); };
  const agregarMedida = (p) => {
    if (camaraMovida || !mG) return;
    setVista(null);
    setMensaje(null);
    setCielo(false);
    const enPiso = !!alPiso(mG, aCal(p));
    const alPisoONada = () => { if (!enPiso) { setCielo(true); return false; } return true; };
    if (herramienta === 'distancia') {
      if (esperaBase != null) {
        if (!alPisoONada()) return;
        setBases((b) => b.map((x, i) => (i === esperaBase ? p : x)));
        setEsperaBase(null);
        return;
      }
      if (pts.length >= TIPOS.distancia.max) return;
      setPts((l) => [...l, p]);
      setBases((b) => [...b.slice(0, pts.length), null]);
      // más arriba del horizonte: es algo en altura, falta dónde se apoya
      if (!enPiso) setEsperaBase(pts.length);
      return;
    }
    if (herramienta === 'pared') {
      if (linea.length < 2) { if (alPisoONada()) setLinea((l) => [...l, p]); return; }
      setPts((l) => (l.length >= TIPOS.pared.max ? l : [...l, p]));
      return;
    }
    if (herramienta === 'volumen') {
      if (faseVol === 'cima') { setCima(p); return; }
      if (alPisoONada()) setPts((l) => (l.length >= TIPOS.volumen.max ? l : [...l, p]));
      return;
    }
    if (herramienta === 'altura') {
      if (pts.length !== 1 && !alPisoONada()) return;
      setPts((l) => (l.length >= 2 ? [p] : [...l, p]));
      return;
    }
    if (alPisoONada()) setPts((l) => (l.length >= TIPOS.area.max ? l : [...l, p]));
  };
  const moverMedida = (ref, p) => {
    const [g, i] = ref;
    if (g === 'medida') setPts((l) => l.map((q, k) => (k === i ? p : q)));
    else if (g === 'mbase') setBases((l) => l.map((q, k) => (k === i ? p : q)));
    else if (g === 'mlinea') setLinea((l) => l.map((q, k) => (k === i ? p : q)));
    else if (g === 'mcima') setCima(p);
  };
  const deshacer = () => {
    setCielo(false);
    if (herramienta === 'volumen') { if (cima) setCima(null); else if (faseVol === 'cima') setFaseVol('base'); else setPts((l) => l.slice(0, -1)); return; }
    if (herramienta === 'pared') { if (pts.length) setPts((l) => l.slice(0, -1)); else setLinea((l) => l.slice(0, -1)); return; }
    if (herramienta === 'distancia') {
      const i = pts.length - 1;
      if (i < 0) return;
      if (esperaBase == null && bases[i]) { setBases((b) => b.map((x, k) => (k === i ? null : x))); return; }
      setPts((l) => l.slice(0, -1));
      setBases((b) => b.slice(0, i));
      setEsperaBase(null);
      return;
    }
    setPts((l) => l.slice(0, -1));
  };
  const cambiarHerramienta = (t) => { setHerramienta(t); limpiar(); setRepetir(null); setVista(null); setMensaje(null); };
  const ultimoEnAltura = () => { const i = pts.length - 1; if (i < 0) return; setBases((b) => b.map((x, k) => (k === i ? null : x))); setEsperaBase(i); };

  const guardarLaMedida = async () => {
    if (!resultado || !(resultado.valor > 0)) return;
    const hoy = fechaObra(foto?.date).slice(0, 5);
    const n = nombre.trim() || repetir?.nombre || `${TIPOS[herramienta].nombre}${hoy ? ` del ${hoy}` : ''}`;
    setGuardando(true);
    setMensaje(null);
    try {
      const d = await guardarMedida(feedId, {
        nombre: n,
        tipo: herramienta,
        pts: ptsCal.map(red),
        ...(herramienta === 'distancia' && bases.some(Boolean) ? { bases: bases.map((x) => (x ? red(aCal(x)) : null)) } : {}),
        ...(herramienta === 'pared' ? { linea: linea.map((x) => red(aCal(x))) } : {}),
        ...(herramienta === 'volumen' ? { cima: red(aCal(cima)), forma } : {}),
        valor: resultado.valor,
        err: Number.isFinite(resultado.err) ? resultado.err : null,
        foto: { fecha: foto.date || fechaDeUrl(foto.url) || '', ref: refDeUrl(foto.url) },
        ...(repetir ? { serie: repetir.serie } : {}),
      });
      setDatos(d);
      if (repetir) setAbiertas((a) => ({ ...a, [repetir.serie]: true }));
      limpiar();
      setNombre('');
      setRepetir(null);
      setMensaje({ tono: 'green', texto: `Guardada: ${n}.` });
    } catch (e) {
      setMensaje({ tono: 'red', texto: errorMedir(e) });
    } finally {
      setGuardando(false);
    }
  };

  const series = useMemo(() => {
    const m = new Map();
    for (const x of datos?.medidas || []) {
      if (!m.has(x.serie)) m.set(x.serie, []);
      m.get(x.serie).push(x);
    }
    return [...m.values()].map((l) => l.slice().sort((a, b) => String(a.foto?.fecha).localeCompare(String(b.foto?.fecha)))).sort((a, b) => String(b[b.length - 1].creadoAt).localeCompare(String(a[a.length - 1].creadoAt)));
  }, [datos]);

  const repetirMedida = (m) => {
    const ai = (p) => [...aImg(p), p[2]];
    setHerramienta(m.tipo);
    setPts(m.pts.map(ai));
    setBases(m.pts.map((_, i) => (m.bases?.[i] ? ai(m.bases[i]) : null)));
    setEsperaBase(null);
    setLinea((m.linea || []).map(ai));
    setCima(m.cima ? ai(m.cima) : null);
    setForma(m.forma || 'pila');
    setFaseVol(m.cima ? 'cima' : 'base');
    setCielo(false);
    setRepetir(m);
    setNombre('');
    setVista(null);
    setMensaje(null);
  };
  const quitarMedida = async (m) => {
    if (!window.confirm(`¿Borrar "${m.nombre}" del ${fechaObra(m.foto?.fecha)}?`)) return;
    try { setDatos(await borrarMedida(feedId, m.id)); } catch (e) { setMensaje({ tono: 'red', texto: errorMedir(e) }); }
  };
  const confirmarNombre = async () => {
    if (!renombrando) return;
    try { setDatos(await renombrarMedida(feedId, renombrando.id, renombrando.nombre)); setRenombrando(null); } catch (e) { setMensaje({ tono: 'red', texto: errorMedir(e) }); }
  };

  // ---- capas a dibujar (en px de la foto que se ve) ----
  const capas = useMemo(() => {
    const c = [];
    if (modo === 'calibrar') {
      const b = borrador;
      const m = ajusteB?.ok ? ajusteB.modelo : null;
      if (m && paso === 'ver') {
        for (const t of baldosasPiso(m, calB.rect, ajusteB.signo, { margen: Math.max(4, 1.5 * Math.max(calB.rect.a, calB.rect.b || ajusteB.bEstimado || 0)) })) c.push({ tipo: 'linea', pts: t.pts, cerrada: true, relleno: t.par ? '#ffffff' : '#2563eb', opRelleno: 0.26, color: '#ffffff', ancho: 1, opacidad: 0.75 });
      }
      const r = b.rect.pts;
      if (r.length === 4) {
        // lados en el orden elegido: largo (rojo) y ancho (azul), con su medida escrita
        const o = calB ? calB.rect.pts : ordenarEsquinas(r);
        const lp = cals && elegido ? cals[elegido.i].largoPrimero : true; // ¿el lado 1-2 es el largo?
        const centro = [o.reduce((a, q) => a + q[0], 0) / 4, o.reduce((a, q) => a + q[1], 0) / 4];
        const lado = (i, j, color, texto) => {
          // con la cámara ya ubicada, el lado se dibuja siguiendo la curva del lente
          c.push({ tipo: 'linea', pts: (m && tramoPiso(m, o[i], o[j])) || [o[i], o[j]], color, ancho: 4 });
          if (texto && paso === 'esquinas') c.push({ tipo: 'texto', p: [(o[i][0] + o[j][0]) / 2, (o[i][1] + o[j][1]) / 2], texto, color, tam: 16, ancla: 'middle', lejosDe: centro, sep: 30 });
        };
        const largo = numero(b.rect.largo);
        const ancho = b.rect.anchoNoSe ? null : numero(b.rect.ancho);
        const tLargo = `Largo${largo > 0 ? ` ${fmtNum(largo, decimales(largo))} m` : ''}`;
        const tAncho = b.rect.anchoNoSe ? 'Ancho: no sé' : `Ancho${ancho > 0 ? ` ${fmtNum(ancho, decimales(ancho))} m` : ''}`;
        lado(0, 1, lp ? COLOR.largo : COLOR.ancho, lp ? tLargo : tAncho);
        lado(2, 3, lp ? COLOR.largo : COLOR.ancho, null);
        lado(1, 2, lp ? COLOR.ancho : COLOR.largo, lp ? tAncho : tLargo);
        lado(3, 0, lp ? COLOR.ancho : COLOR.largo, null);
        c.push({ tipo: 'linea', pts: o, cerrada: true, color: 'transparent', relleno: '#2563eb', ancho: 0, opRelleno: paso === 'ver' ? 0 : 0.18 });
      } else if (r.length >= 2) {
        c.push({ tipo: 'linea', pts: r, color: '#2563eb', ancho: 3, guiones: true });
      }
      r.forEach((p, i) => c.push({ tipo: 'punto', p, color: '#2563eb', etiqueta: String(i + 1), ref: paso !== 'alto' ? ['rect', i] : null }));
      if (paso === 'alto' || (conAlto && paso === 'ver')) {
        const v = b.vertical;
        if (v.base && m && !v.punta) {
          const gv = guiaVertical(m, v.base);
          if (gv) c.push({ tipo: 'linea', pts: gv, color: COLOR.vertical, ancho: 1.5, guiones: true });
        }
        if (v.base && v.punta) c.push({ tipo: 'linea', pts: (m && dibujoMedida(m, 'altura', [v.base, v.punta])?.lineas[0]) || [v.base, v.punta], color: COLOR.vertical, ancho: 4 });
        if (v.base) c.push({ tipo: 'punto', p: v.base, color: COLOR.vertical, forma: 'cuadrado', etiqueta: paso === 'alto' ? 'abajo' : null, ref: paso === 'alto' ? ['base'] : null });
        if (v.punta) c.push({ tipo: 'punto', p: v.punta, color: COLOR.vertical, etiqueta: paso === 'alto' ? 'arriba' : null, ref: paso === 'alto' ? ['punta'] : null });
      }
      if (paso === 'plomadas' || (paso === 'ver' && b.plomadas.length)) {
        b.plomadas.forEach((x, i) => {
          if (x.base && !x.punta && m) { const gv = guiaVertical(m, x.base); if (gv) c.push({ tipo: 'linea', pts: gv, color: COLOR.plomada, ancho: 1.5, guiones: true }); }
          if (x.base && x.punta) {
            const curva = m && dibujoMedida(m, 'altura', [x.base, x.punta])?.lineas[0];
            // lo que marcó el cliente (fino) y la vertical que da el piso (gruesa): si no coinciden, el piso está torcido
            c.push({ tipo: 'linea', pts: [x.base, x.punta], color: '#ffffff', ancho: 1.5, guiones: true });
            if (curva) c.push({ tipo: 'linea', pts: curva, color: COLOR.plomada, ancho: 3.5 });
          }
          if (x.base) c.push({ tipo: 'punto', p: x.base, color: COLOR.plomada, forma: 'cuadrado', etiqueta: paso === 'plomadas' ? `${i + 1} abajo` : null, ref: paso === 'plomadas' ? ['pb', i] : null });
          if (x.punta) c.push({ tipo: 'punto', p: x.punta, color: COLOR.plomada, etiqueta: paso === 'plomadas' ? `${i + 1} arriba` : null, ref: paso === 'plomadas' ? ['pp', i] : null });
        });
      }
      return c;
    }
    if (modo === 'medir' && mG) {
      const etiquetas = [];
      const txt = (tipo, v, err) => {
        const d = decimales(v);
        return { texto: `${fmtNum(v, d)} ${TIPOS[tipo]?.unidad || 'm'}`, sub: err != null && Number.isFinite(err) ? `± ${fmtNum(err, d)}` : null };
      };
      // P y extra en px de la calibración; se dibuja en px de la foto que se ve
      const dibujar = (tipo, P, extra, color, valor) => {
        const d = dibujoMedida(mG, tipo, P, extra);
        if (!d) return;
        if ((tipo === 'area' || tipo === 'volumen') && d.lineas.length >= 3) c.push({ tipo: 'linea', pts: d.lineas.flat().map(aImg), cerrada: true, relleno: color, opRelleno: 0.2, color: 'transparent', ancho: 0 });
        d.verticales.forEach((l) => c.push({ tipo: 'linea', pts: l.map(aImg), color, ancho: 2, guiones: true }));
        d.lineas.forEach((l) => c.push({ tipo: 'linea', pts: l.map(aImg), color, ancho: 4 }));
        if (valor && d.etiqueta) etiquetas.push({ tipo: 'etiqueta', p: aImg(d.etiqueta), color, tam: tipo === 'volumen' ? 18 : 15, ...(tipo === 'altura' ? { arriba: 0, costado: true } : {}), ...valor });
      };
      if (vista) {
        dibujar(vista.tipo, vista.pts, { bases: vista.bases || [], linea: vista.linea || [], cima: vista.cima, forma: vista.forma }, COLOR.vista, txt(vista.tipo, vista.valor, vista.err));
        vista.pts.forEach((p) => c.push({ tipo: 'punto', p: aImg(p), color: COLOR.vista, chico: true }));
      }
      // lo que se está midiendo
      if (herramienta === 'pared') {
        if (linea.length === 2) { const l = tramoPiso(mG, extraCal.linea[0], extraCal.linea[1]); if (l) c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.pared, ancho: 3, guiones: true }); }
        else if (linea.length === 1 && cursorP) { const l = tramoPiso(mG, extraCal.linea[0], aCal(cursorP)); if (l) c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.pared, ancho: 2, guiones: true }); }
        linea.forEach((p, i) => c.push({ tipo: 'punto', p, color: COLOR.pared, forma: 'cuadrado', etiqueta: 'pie', ref: ['mlinea', i] }));
      }
      if (herramienta === 'altura' && pts.length === 1) {
        const gv = guiaVertical(mG, ptsCal[0]);
        if (gv) c.push({ tipo: 'linea', pts: gv.map(aImg), color: COLOR.medida, ancho: 1.5, guiones: true });
      }
      if (herramienta === 'volumen' && pts.length >= 2 && !completa) {
        const d = dibujoMedida(mG, 'area', pts.length >= 3 ? ptsCal : ptsCal, {});
        if (d) d.lineas.forEach((l) => c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.medida, ancho: 3 }));
      }
      if (completa) dibujar(herramienta, ptsCal, extraCal, COLOR.medida, resultado?.fuera ? { texto: 'Sin precisión acá', sub: null } : resultado?.valor > 0 ? txt(herramienta, resultado.valor, vecG.listo ? resultado.err : null) : null);
      else if (herramienta === 'distancia' && pts.length >= 2) {
        // falta dónde se apoya un punto en altura: se dibujan los tramos que ya se pueden ubicar
        for (let i = 1; i < pts.length; i++) if (i !== esperaBase && i - 1 !== esperaBase) { const l = tramoPiso(mG, ptsCal[i - 1], ptsCal[i]); if (l) c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.medida, ancho: 3 }); }
      } else if (herramienta === 'area' && pts.length === 2) { const l = tramoPiso(mG, ptsCal[0], ptsCal[1]); if (l) c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.medida, ancho: 3 }); }
      if (previa) {
        previa.d.lineas.forEach((l) => c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.medida, ancho: 2.5, guiones: true, opacidad: 0.9 }));
        previa.d.verticales.forEach((l) => c.push({ tipo: 'linea', pts: l.map(aImg), color: COLOR.medida, ancho: 1.5, guiones: true }));
        if (previa.d.etiqueta) etiquetas.push({ tipo: 'etiqueta', p: aImg(previa.d.etiqueta), color: '#15803d', tam: 14, ...(previa.tipo === 'altura' ? { arriba: 0, costado: true } : {}), ...txt(previa.tipo, previa.valor, null) });
      }
      pts.forEach((p, i) => c.push({ tipo: 'punto', p, color: COLOR.medida, forma: herramienta === 'altura' && i === 0 ? 'cuadrado' : undefined, ref: ['medida', i] }));
      bases.forEach((p, i) => p && c.push({ tipo: 'punto', p, color: COLOR.medida, forma: 'cuadrado', ref: ['mbase', i] }));
      if (cima) c.push({ tipo: 'punto', p: cima, color: COLOR.medida, etiqueta: forma === 'pozo' ? 'fondo' : 'punta', ref: ['mcima'] });
      c.push(...etiquetas);
    }
    return c;
  }, [modo, borrador, ajusteB, calB, cals, elegido, paso, conAlto, mG, vista, pts, ptsCal, bases, linea, cima, forma, extraCal, completa, resultado, vecG.listo, previa, cursorP, esperaBase, herramienta, aCal, aImg]);

  // ---- render ----
  if (!feedId) return null;
  const cargandoFoto = !img && !fotoError;
  const abrirTutorial = () => setTutorial({ congelar: null });

  const cabeceraFoto = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 mb-2">
      <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> Foto del {fechaObra(foto?.date) || '—'}</span>
      <button type="button" onClick={() => picker.open(foto?.date ? new Date(foto.date) : new Date())} className="text-blue-700 hover:underline font-medium" disabled={modo === 'calibrar' && paso !== 'esquinas'}>
        Elegir otra foto
      </button>
      <button type="button" onClick={abrirTutorial} className="ml-auto inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100">
        <PlayCircle className="w-4 h-4" /> ¿Cómo funciona?
      </button>
    </div>
  );

  // resultado grande sobre la foto
  let cartel = null;
  if (modo === 'medir') {
    const mostrar = resultado?.valor > 0 ? { tipo: herramienta, valor: resultado.valor, err: vecG.listo ? resultado.err : null, inestable: resultado.fuera, color: resultado.fuera ? '#64748b' : COLOR.medida, nombre: null }
      : vista ? { tipo: vista.tipo, valor: vista.valor, err: vista.err, color: COLOR.vista, nombre: vista.nombre } : null;
    if (mostrar) {
      const d = decimales(mostrar.valor);
      const u = TIPOS[mostrar.tipo]?.unidad || 'm';
      cartel = (
        <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 z-20 rounded-2xl px-4 py-2 text-white shadow-lg text-center" style={{ background: mostrar.color }}>
          {mostrar.nombre && <p className="text-xs font-semibold opacity-90 truncate max-w-[60vw]">{mostrar.nombre}</p>}
          <p className="leading-none whitespace-nowrap">
            {mostrar.inestable ? <span className="text-lg sm:text-xl font-extrabold">Sin precisión en ese lugar</span> : <span className="text-2xl sm:text-3xl font-extrabold">{fmtNum(mostrar.valor, d)} {u}</span>}
            {mostrar.inestable ? null : mostrar.err != null && Number.isFinite(mostrar.err) ? <span className="ml-2 text-lg sm:text-xl font-bold opacity-90">± {fmtNum(mostrar.err, d)}</span> : null}
          </p>
        </div>
      );
    }
  }

  const foto4k = img ? (
    <MedirFoto
      url={img.url}
      w={img.w}
      h={img.h}
      rot={rotacion}
      capas={capas}
      onAgregar={modo === 'calibrar' && paso !== 'ver' ? agregarCalibracion : modo === 'medir' && ajusteG?.ok ? agregarMedida : undefined}
      onMover={modo === 'calibrar' ? moverCalibracion : modo === 'medir' ? moverMedida : undefined}
      onPuntero={modo === 'medir' ? setCursorP : undefined}
      cursor={modo === 'calibrar' || modo === 'medir' ? 'crosshair' : 'default'}
    >
      {cartel}
    </MedirFoto>
  ) : (
    <div className="w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-500 gap-2">
      {cargandoFoto ? <><Loader2 className="w-5 h-5 animate-spin" /> Cargando la foto…</> : fotoError}
    </div>
  );

  let panel = null;
  if (modo === 'cargando') {
    panel = <div className="flex items-center gap-2 text-sm text-gray-500 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  } else if (modo === 'intro') {
    panel = (
      <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-4 sm:p-5 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Medí la obra sobre la foto</h3>
          <p className="text-sm text-gray-600">Distancias, superficies y alturas en metros. Antes, calibrás la cámara una sola vez (1 minuto).</p>
        </div>
        <ol className="grid grid-cols-3 gap-2 text-center">
          {[
            { I: Crosshair, t: 'Tocá 4 esquinas de algo del piso' },
            { I: Pencil, t: 'Escribí cuánto mide' },
            { I: Ruler, t: 'Medí lo que quieras' },
          ].map(({ I, t }, i) => (
            <li key={t} className="rounded-xl bg-white border border-gray-100 p-2.5 sm:p-3">
              <div className="mx-auto mb-1.5 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center"><I className="w-5 h-5" /></div>
              <p className="text-xs sm:text-sm font-semibold text-gray-800"><span className="text-blue-600">{i + 1}.</span> {t}</p>
            </li>
          ))}
        </ol>
        {errorCarga && <Aviso tono="red">{errorCarga}</Aviso>}
        <div className="flex flex-wrap gap-2">
          <Boton variante="pri" grande onClick={() => empezarCalibracion(false)} disabled={!img}><Crosshair className="w-5 h-5" /> Calibrar la cámara</Boton>
          <Boton grande onClick={abrirTutorial}><PlayCircle className="w-5 h-5" /> Ver cómo se hace</Boton>
        </div>
      </div>
    );
  } else if (modo === 'calibrar') {
    const b = borrador;
    const setRect = (k, v) => setBorrador((x) => ({ ...x, rect: { ...x.rect, [k]: v } }));
    const setVert = (k, v) => setBorrador((x) => ({ ...x, vertical: { ...x.vertical, [k]: v } }));
    const nPts = b.rect.pts.length;
    const largo = numero(b.rect.largo);
    const ancho = numero(b.rect.ancho);
    const medidasOk = largo > 0 && (b.rect.anchoNoSe || ancho > 0);
    const necesitaAlto = b.rect.anchoNoSe;
    const pasos = necesitaAlto || conAlto ? ['Marcar el piso', 'Algo parado', 'Enderezar', 'Revisar'] : ['Marcar el piso', 'Enderezar', 'Revisar'];
    const iPaso = paso === 'esquinas' ? 0 : paso === 'alto' ? 1 : paso === 'plomadas' ? pasos.length - 2 : pasos.length - 1;
    const chico = nPts === 4 && img && areaPx(ordenarEsquinas(b.rect.pts)) / (img.w * img.h) < 0.004;
    const errorRect = nPts === 4 && medidasOk && !necesitaAlto && ajustes && !ajustes.some((a) => a.ok) ? 'No pudimos ubicar el piso con esas esquinas: revisá que estén sobre el piso y que las medidas sean las de ese rectángulo.' : null;
    let contenido = null;
    let pie = null;
    if (paso === 'esquinas') {
      const puede = nPts === 4 && medidasOk && (necesitaAlto || ajusteB?.ok);
      contenido = (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-4 items-start">
          <div className="space-y-3 min-w-0">
            <p className="text-base font-semibold text-gray-900">Tocá en la foto las 4 esquinas de algo del piso que sepas cuánto mide.</p>
            <p className="text-sm text-gray-600">Una platea, un contenedor, una vereda. <b>Mejor grande y cerca de lo que vas a medir.</b> Hacé zoom para tocar justo.</p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1.5" aria-label={`${nPts} de 4 esquinas`}>
                {[0, 1, 2, 3].map((i) => <span key={i} className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${i < nPts ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-400'}`}>{i < nPts ? <Check className="w-4 h-4" /> : i + 1}</span>)}
              </div>
              <Boton className="ml-auto" onClick={() => setRect('pts', b.rect.pts.slice(0, -1))} disabled={!nPts}><Undo2 className="w-4 h-4" /> Deshacer</Boton>
              {nPts > 1 && <Boton onClick={() => { setBorrador(borradorVacio()); setConAlto(false); }}>Borrar todo</Boton>}
            </div>
            {chico && <Aviso>Es chico para la foto. Si hay algo más grande cerca de lo que querés medir, usalo: mide mejor.</Aviso>}
            <div className={nPts < 4 ? 'opacity-50 pointer-events-none' : ''}>
              <p className="text-sm font-semibold text-gray-800 mb-1.5">¿Cuánto mide?</p>
              <div className="flex flex-wrap gap-2">
                <CampoGrande id="medir-largo" inputRef={largoRef} label="Largo (lado rojo)" color={COLOR.largo} value={b.rect.largo} onChange={(v) => setRect('largo', v)} />
                <CampoGrande id="medir-ancho" label="Ancho (lado azul)" color={COLOR.ancho} value={b.rect.anchoNoSe ? '' : b.rect.ancho} placeholder={b.rect.anchoNoSe ? 'No sé' : '0,0'} deshabilitado={b.rect.anchoNoSe} onChange={(v) => setRect('ancho', v)} />
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {EJEMPLOS_RECT.map((e) => (
                  <button key={e.t} type="button" className="px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-sm text-gray-700" onClick={() => setBorrador((x) => ({ ...x, rect: { ...x.rect, largo: txtNum(e.a), ancho: txtNum(e.b), anchoNoSe: false, exacta: false } }))}>
                    {e.t} <b>{e.d}</b>
                  </button>
                ))}
                <button type="button" className={`px-3 py-1.5 rounded-full border text-sm ${b.rect.exacta ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`} onClick={() => { setRect('exacta', !b.rect.exacta); largoRef.current?.focus(); }}>
                  {b.rect.exacta && <Check className="inline w-3.5 h-3.5 mr-0.5" />}Lo saco de los planos
                </button>
                <button type="button" className={`px-3 py-1.5 rounded-full border text-sm ${b.rect.anchoNoSe ? 'border-amber-500 bg-amber-50 text-amber-800 font-semibold' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`} onClick={() => setRect('anchoNoSe', !b.rect.anchoNoSe)}>
                  {b.rect.anchoNoSe && <Check className="inline w-3.5 h-3.5 mr-0.5" />}No sé el ancho
                </button>
              </div>
              {nPts === 4 && medidasOk && !b.rect.anchoNoSe && elegido?.ambas && (
                <button type="button" onClick={() => setRect('girar', !b.rect.girar)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline">
                  <ArrowLeftRight className="w-4 h-4" /> ¿El largo y el ancho quedaron al revés en la foto? Cambialos
                </button>
              )}
              {necesitaAlto && <p className="mt-2 text-sm text-amber-800">Sin el ancho, en el paso siguiente vas a tocar algo parado que sepas cuánto mide.</p>}
            </div>
            {errorRect && <Aviso tono="red">{errorRect}</Aviso>}
          </div>
          <div className="hidden md:block rounded-xl border border-gray-100 bg-white p-2">
            <DibujoRectangulo />
            <p className="text-xs text-gray-500 text-center mt-1">Grande y cerca <span className="text-green-700 font-semibold">sí</span> · chico y lejos <span className="text-red-600 font-semibold">no</span></p>
          </div>
        </div>
      );
      pie = (
        <>
          <Boton onClick={() => setModo(calibracion ? 'medir' : 'intro')}>Cancelar</Boton>
          <Boton variante="pri" grande onClick={() => setPaso(necesitaAlto || conAlto ? 'alto' : 'plomadas')} disabled={!puede}>Seguir</Boton>
        </>
      );
    } else if (paso === 'alto') {
      const v = b.vertical;
      const listo = v.base && v.punta && numero(v.alto) > 0;
      contenido = (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_170px] gap-4 items-start">
          <div className="space-y-3 min-w-0">
            <p className="text-base font-semibold text-gray-900">Tocá abajo y arriba de algo parado que sepas cuánto mide.</p>
            <p className="text-sm text-gray-600">Primero donde apoya en el piso, después la punta. Una puerta, un baño químico, una persona.</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-sm font-semibold ${v.base ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>{v.base ? <Check className="inline w-4 h-4" /> : '1'} Abajo</span>
              <span className={`rounded-full px-3 py-1 text-sm font-semibold ${v.punta ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>{v.punta ? <Check className="inline w-4 h-4" /> : '2'} Arriba</span>
              <Boton className="ml-auto" onClick={() => setBorrador((x) => ({ ...x, vertical: { ...x.vertical, ...(x.vertical.punta ? { punta: null } : { base: null }) } }))} disabled={!v.base}><Undo2 className="w-4 h-4" /> Deshacer</Boton>
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <CampoGrande id="medir-alto" label="Alto" color={COLOR.vertical} value={v.alto} onChange={(x) => setVert('alto', x)} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EJEMPLOS_ALTO.map((e) => (
                <button key={e.t} type="button" className={`px-3 py-1.5 rounded-full border text-sm ${numero(v.alto) === e.v ? 'border-amber-500 bg-amber-50 text-amber-900 font-semibold' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`} onClick={() => setBorrador((x) => ({ ...x, vertical: { ...x.vertical, alto: txtNum(e.v), exacta: false } }))}>
                  {e.t} <b>{fmtNum(e.v)} m</b>
                </button>
              ))}
            </div>
            {listo && ajusteB && !ajusteB.ok && <Aviso tono="red">{ajusteB.error}</Aviso>}
          </div>
          <div className="hidden md:block rounded-xl border border-gray-100 bg-white p-2"><DibujoAltura /></div>
        </div>
      );
      pie = (
        <>
          <Boton onClick={() => setPaso('esquinas')}>Atrás</Boton>
          {!necesitaAlto && <Boton onClick={() => { setBorrador((x) => ({ ...x, vertical: { base: null, punta: null, alto: '', exacta: false } })); setConAlto(false); setPaso('plomadas'); }}>No, gracias</Boton>}
          <Boton variante="pri" grande onClick={() => setPaso('plomadas')} disabled={!(listo && ajusteB?.ok)}>Seguir</Boton>
        </>
      );
    } else if (paso === 'plomadas') {
      const l = b.plomadas;
      const nOk = plomadasOk.length;
      const u = l[l.length - 1];
      const falta = !u || u.punta ? (l.length < 2 ? 'abajo' : null) : 'arriba';
      contenido = (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_170px] gap-4 items-start">
          <div className="space-y-3 min-w-0">
            <p className="text-base font-semibold text-gray-900">Para más precisión: marcá 1 o 2 cosas verticales.</p>
            <p className="text-sm text-gray-600">Un poste, la esquina de un edificio, una columna. Tocá <b>abajo</b>, donde apoya en el piso, y después <b>arriba</b>, sobre la misma cosa. No hace falta saber cuánto mide. Es opcional.</p>
            <div className="flex flex-wrap items-center gap-2">
              {[0, 1].map((i) => {
                const x = l[i];
                const ok = x?.base && x?.punta;
                return <span key={i} className={`rounded-full px-3 py-1 text-sm font-semibold ${ok ? 'bg-cyan-600 text-white' : 'bg-cyan-50 text-cyan-800 border border-cyan-200'}`}>{ok ? <Check className="inline w-4 h-4" /> : <UtilityPole className="inline w-4 h-4" />} {i + 1}ª cosa{x?.base && !x?.punta ? ': falta arriba' : ''}</span>;
              })}
              <Boton className="ml-auto" onClick={() => setBorrador((x) => { const k = x.plomadas.slice(); const z = k[k.length - 1]; if (z?.punta) k[k.length - 1] = { ...z, punta: null }; else k.pop(); return { ...x, plomadas: k }; })} disabled={!l.length}><Undo2 className="w-4 h-4" /> Deshacer</Boton>
            </div>
            {falta && <p className="text-sm font-semibold text-cyan-800">{falta === 'abajo' ? `Tocá abajo de la ${l.length + 1}ª cosa vertical.` : 'Ahora tocá arriba, sobre la misma cosa (la línea punteada te guía).'}</p>}
            {pendiente?.pendiente && <Aviso>El piso que marcaste parece en pendiente (lo vertical se aparta {fmtNum(pendiente.max, 0)}°). Marcá otro rectángulo más plano o sumá otra cosa vertical.</Aviso>}
            {pendiente && !pendiente.pendiente && <Aviso tono="green">Bien: lo vertical coincide con el piso que marcaste.</Aviso>}
          </div>
          <div className="hidden md:block rounded-xl border border-gray-100 bg-white p-2"><DibujoPlomada /></div>
        </div>
      );
      pie = (
        <>
          <Boton onClick={() => setPaso(necesitaAlto || conAlto ? 'alto' : 'esquinas')}>Atrás</Boton>
          <Boton variante="pri" grande onClick={() => setPaso('ver')} disabled={!!(u && !u.punta)}>{nOk ? 'Seguir' : 'Saltear'}</Boton>
        </>
      );
    } else if (paso === 'ver') {
      const q = calidadB;
      const pp = q ? precisionEnPalabras(q.precisionPct) : null;
      contenido = (
        <div className="space-y-3">
          <p className="text-base font-semibold text-gray-900">Mirá las baldosas de la foto: cada una mide 1 × 1 m.</p>
          <p className="text-sm text-gray-600">¿Se ven del tamaño correcto y apoyadas sobre el piso? Si algo no cierra, volvé y corregí las esquinas o las medidas (también podés arrastrar las esquinas acá).</p>
          {!ajusteB?.ok ? (
            <Aviso tono="red">{ajusteB?.error || 'Faltan datos.'}</Aviso>
          ) : !q ? (
            <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Calculando la precisión…</p>
          ) : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {pp && <span>Precisión cerca de lo que marcaste: <b className={pp.color}>{pp.txt}</b> <span className="text-gray-500">({pp.pct})</span>{pp.txt === 'baja' && <span className="text-gray-600"> · para mejorarla, marcá algo más grande y más cerca</span>}</span>}
              {ajusteB.bEstimado && <span className="text-gray-600">Ancho calculado: <b>{fmtNum(ajusteB.bEstimado)} m</b></span>}
            </div>
          )}
          {pendiente?.pendiente && <Aviso>El piso que marcaste parece en pendiente: marcá otro rectángulo más plano o sumá otra cosa vertical.</Aviso>}
          {q?.avisos?.length > 0 && <Aviso>Algo no cierra: revisá que las 4 esquinas estén bien tocadas, sobre el piso, y que las medidas sean las de esa cosa. Si las baldosas se ven raras, tocá «No, corregir».</Aviso>}
          {!conAlto && !necesitaAlto && (
            <button type="button" onClick={() => { setConAlto(true); setPaso('alto'); }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline">
              <MoveVertical className="w-4 h-4" /> ¿Vas a medir alturas? Tocá algo parado y mejora la precisión
            </button>
          )}
        </div>
      );
      pie = (
        <>
          <Boton onClick={() => setPaso('plomadas')}>No, corregir</Boton>
          <Boton variante="ok" grande onClick={guardarCal} disabled={!ajusteB?.ok || guardando}>
            {guardando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Se ve bien
          </Boton>
        </>
      );
    }
    panel = (
      <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-bold text-gray-900 mr-auto">Calibrar la cámara</h3>
          <PasosChips pasos={pasos} actual={iPaso} />
        </div>
        {contenido}
        {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}
        <div className="flex flex-wrap justify-end gap-2 pt-1 border-t border-gray-200">{pie}</div>
      </div>
    );
  } else if (modo === 'medir') {
    const t = TIPOS[herramienta];
    const faltan = Math.max(0, t.min - pts.length);
    const pp = precisionEnPalabras(calidadG?.precisionPct ?? calibracion?.calidad?.precisionPct);
    const enAlto = herramienta === 'distancia' && esperaBase != null;
    const instruccion = {
      distancia: enAlto ? 'Ese punto está en altura: ahora tocá abajo, donde se apoya en el piso.' : !pts.length ? 'Tocá dos puntos apoyados en el piso.' : faltan ? 'Ahora el otro punto.' : 'Podés seguir tocando para sumar tramos.',
      area: !pts.length ? 'Tocá las esquinas de la superficie (apoyada en el piso), dando la vuelta.' : faltan ? `Te falta${faltan > 1 ? 'n' : ''} ${faltan} esquina${faltan > 1 ? 's' : ''}.` : 'Seguí tocando si tiene más esquinas.',
      altura: !pts.length ? 'Tocá primero abajo, donde apoya en el piso.' : faltan ? 'Ahora tocá arriba, sobre la línea punteada.' : 'Listo. Podés arrastrar los puntos.',
      volumen: faseVol === 'base' ? (pts.length < 3 ? 'Tocá el borde de la base, apoyado en el piso, dando la vuelta.' : 'Seguí el borde o tocá «Listo el borde».') : !cima ? (forma === 'pozo' ? 'Ahora tocá el fondo del pozo, en el medio.' : 'Ahora tocá la punta más alta.') : 'Listo. Podés arrastrar los puntos.',
      pared: linea.length < 2 ? 'Primero tocá 2 puntos al pie de la pared, donde toca el piso.' : pts.length < 2 ? 'Ahora tocá lo que quieras medir sobre esa pared.' : 'Podés seguir tocando para sumar tramos.',
    }[herramienta];
    panel = (
      <div className="space-y-3">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" role="tablist" aria-label="Qué querés medir">
          {[
            { id: 'distancia', label: 'Distancia', Icon: Ruler },
            { id: 'area', label: 'Superficie', Icon: Square },
            { id: 'altura', label: 'Altura', Icon: MoveVertical },
            { id: 'volumen', label: 'Volumen', Icon: Mountain },
            { id: 'pared', label: 'En pared', Icon: BrickWall },
          ].map(({ id, label, Icon }) => (
            <button key={id} type="button" role="tab" aria-selected={herramienta === id} onClick={() => cambiarHerramienta(id)} className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 rounded-2xl px-2 py-3 text-sm sm:text-base font-bold transition-colors active:scale-95 ${herramienta === id ? 'bg-blue-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              <Icon className="w-6 h-6" /> {label}
            </button>
          ))}
        </div>

        {encuadre?.estado === 'moved' && <Aviso tono="red">La cámara cambió de lugar desde que la calibraste ({fechaObra(calibracion.foto.fecha)}). Para medir en esta foto hay que <button type="button" className="font-semibold underline" onClick={() => empezarCalibracion(false)}>volver a calibrar</button>, o elegí una foto anterior.</Aviso>}
        {encuadre?.estado === 'aligned' && <Aviso>La cámara se corrió un poco desde la calibración: las medidas pueden tener más error. Conviene recalibrar.</Aviso>}
        {encuadre?.estado === 'verificando' && <p className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Revisando que la cámara no se haya movido…</p>}
        {encuadre?.estado === 'sin-dato' && <p className="text-xs text-gray-500">No pudimos comparar esta foto con la de la calibración: si la cámara se movió, recalibrá.</p>}

        {repetir && (
          <Aviso tono="blue" icon={History}>
            Volviendo a medir <b>{repetir.nombre}</b> (antes: {fmtMedida(repetir.tipo, repetir.valor, repetir.err)} el {fechaObra(repetir.foto?.fecha)}). Arrastrá los puntos a donde está hoy y guardá. <button type="button" className="underline" onClick={() => { setRepetir(null); setPts([]); }}>Cancelar</button>
          </Aviso>
        )}

        <p className="flex items-center gap-1.5 text-xs text-gray-500"><Info className="w-3.5 h-3.5 shrink-0" /> Medimos exacto sobre el piso que calibraste y en cosas apoyadas en él.</p>

        <div className="rounded-2xl border border-gray-200 bg-white p-3 space-y-2">
          <p className="text-base font-semibold text-gray-800">{instruccion}</p>
          {herramienta === 'volumen' && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" role="radiogroup" aria-label="Forma">
                {[
                  { id: 'pila', I: Mountain, d: 'como un cono' },
                  { id: 'alargada', I: MountainSnow, d: 'como un techo' },
                  { id: 'caja', I: Package, d: 'paredes rectas' },
                  { id: 'pozo', I: Shovel, d: 'para abajo' },
                ].map(({ id, I, d }) => (
                  <button key={id} type="button" role="radio" aria-checked={forma === id} onClick={() => { setForma(id); setCielo(false); }} className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 text-left ${forma === id ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                    <I className="w-6 h-6 shrink-0" /><span className="leading-tight"><b className="block text-sm">{FORMAS[id].nombre}</b><span className="text-xs text-gray-500">{d}</span></span>
                  </button>
                ))}
              </div>
              {faseVol === 'base' && pts.length >= 3 && <Boton variante="pri" onClick={() => setFaseVol('cima')}><Check className="w-4 h-4" /> Listo el borde</Boton>}
              <p className="text-xs text-gray-500">Es una estimación desde una sola foto: superficie de la base × alto × la forma que elegiste.</p>
            </div>
          )}
          {herramienta === 'distancia' && pts.length > 0 && !enAlto && !bases[pts.length - 1] && (
            <button type="button" onClick={ultimoEnAltura} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline"><MoveVertical className="w-4 h-4" /> ¿El último punto está en altura? Marcá dónde se apoya</button>
          )}
          {cielo && <AvisoCielo onCerrar={() => setCielo(false)} />}
          {resultado && resultado.valor == null && <Aviso>No pudimos ubicar uno de los puntos: revisá que lo de abajo esté apoyado en el piso.</Aviso>}
          {resultado?.valor != null && resultado.valor <= 0 && herramienta === 'altura' && <Aviso>Tocá primero abajo (donde apoya en el piso) y después arriba.</Aviso>}
          {(resultado?.fuera || resultado?.errPct > 60) && <Aviso>El margen es muy grande: lo que medís está lejos de lo que usaste para calibrar (o muy cerca del horizonte). Para medir ahí, calibrá con algo más grande y más cerca.</Aviso>}
          {(pts.length > 0 || linea.length > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {resultado?.valor > 0 && !resultado.fuera && (
                <>
                  <input
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && guardarLaMedida()}
                    placeholder={repetir ? repetir.nombre : 'Nombre (opcional)'}
                    aria-label="Nombre de la medida (opcional)"
                    maxLength={80}
                    className="flex-1 min-w-[160px] rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                  <Boton variante="pri" onClick={guardarLaMedida} disabled={guardando}>{guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar</Boton>
                </>
              )}
              <Boton onClick={deshacer}><Undo2 className="w-4 h-4" /> Deshacer</Boton>
              <Boton onClick={() => { limpiar(); setRepetir(null); }}>Borrar</Boton>
            </div>
          )}
        </div>
        {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {calibracion && <span>Calibrada el {fechaObra(calibracion.creadoAt).slice(0, 10)}{pp ? <> · precisión <b className={pp.color}>{pp.txt}</b></> : null}</span>}
          <button type="button" onClick={() => empezarCalibracion(true)} className="ml-auto inline-flex items-center gap-1 font-semibold text-gray-600 hover:text-blue-700"><RotateCcw className="w-3.5 h-3.5" /> Volver a calibrar</button>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Medidas guardadas</h3>
            <span className="text-xs text-gray-500">{datos?.medidas?.length || 0}</span>
          </div>
          {!series.length && <p className="px-3 py-3 text-sm text-gray-500">Todavía no guardaste medidas. Medí algo y guardalo: después podés volver a medirlo otro día para ver cómo avanza.</p>}
          <ul className="divide-y divide-gray-100">
            {series.map((serie) => {
              const ult = serie[serie.length - 1];
              const primera = serie[0];
              const delta = serie.length > 1 ? ult.valor - primera.valor : null;
              const abierta = abiertas[ult.serie];
              return (
                <li key={ult.serie} className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <div className="min-w-0 flex-1">
                      {renombrando?.serie === ult.serie ? (
                        <div className="flex gap-1">
                          <input autoFocus value={renombrando.nombre} maxLength={80} onChange={(e) => setRenombrando((r) => ({ ...r, nombre: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') confirmarNombre(); if (e.key === 'Escape') setRenombrando(null); }} className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm" />
                          <Boton onClick={confirmarNombre}><Check className="w-4 h-4" /></Boton>
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-900 truncate">{ult.nombre}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        {TIPOS[ult.tipo]?.nombre} · <b className="text-gray-800">{fmtMedida(ult.tipo, ult.valor, ult.err)}</b> · foto del {fechaObra(ult.foto?.fecha)}
                        {delta != null && <> · <span className={delta >= 0 ? 'text-green-700' : 'text-red-700'}>{delta >= 0 ? '+' : ''}{fmtNum(delta, Math.abs(delta) >= 10 ? 1 : 2)} {ult.unidad}</span> desde el {fechaObra(primera.foto?.fecha).slice(0, 10)}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" title="Ver en la foto" onClick={() => { setVista(vista?.id === ult.id ? null : ult); limpiar(); setRepetir(null); }} className={`p-2 rounded-lg hover:bg-gray-100 ${vista?.id === ult.id ? 'text-purple-700 bg-purple-50' : 'text-gray-600'}`}><Eye className="w-4 h-4" /></button>
                      <button type="button" title="Volver a medir en esta foto" onClick={() => repetirMedida(ult)} disabled={camaraMovida} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40"><RotateCcw className="w-4 h-4" /></button>
                      <button type="button" title="Cambiar el nombre" onClick={() => setRenombrando({ id: ult.id, serie: ult.serie, nombre: ult.nombre })} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
                      {serie.length > 1 && <button type="button" title="Ver cómo cambió" onClick={() => setAbiertas((a) => ({ ...a, [ult.serie]: !a[ult.serie] }))} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"><History className="w-4 h-4" /></button>}
                      {serie.length === 1 && <button type="button" title="Borrar" onClick={() => quitarMedida(ult)} className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                  {abierta && serie.length > 1 && (
                    <ul className="mt-2 ml-1 border-l-2 border-blue-100 pl-3 space-y-1">
                      {serie.map((m, i) => (
                        <li key={m.id} className="flex items-center gap-2 text-xs text-gray-600">
                          <span className="w-28 shrink-0">{fechaObra(m.foto?.fecha)}</span>
                          <b className="text-gray-800">{fmtMedida(m.tipo, m.valor, m.err)}</b>
                          {i > 0 && <span className={m.valor - serie[i - 1].valor >= 0 ? 'text-green-700' : 'text-red-700'}>{m.valor - serie[i - 1].valor >= 0 ? '+' : ''}{fmtNum(m.valor - serie[i - 1].valor, 2)}</span>}
                          <button type="button" title="Ver en la foto" onClick={() => { setVista(m); limpiar(); }} className="ml-auto p-1 rounded hover:bg-gray-100"><Eye className="w-3.5 h-3.5" /></button>
                          <button type="button" title="Borrar" onClick={() => quitarMedida(m)} className="p-1 rounded hover:bg-red-50 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {cabeceraFoto}
      {foto4k}
      {panel}
      <Limites />
      {tutorial && (
        <MedirTutorial
          congelar={tutorial.congelar}
          onCerrar={() => { marcarVisto(); setTutorial(null); }}
          onEmpezar={modo === 'intro' && img ? () => { marcarVisto(); setTutorial(null); empezarCalibracion(false); } : undefined}
        />
      )}
      {picker.isOpen && (
        <DatePickerModal
          title="Elegí la foto para medir"
          user={user}
          picker={picker}
          onSelectImage={(image) => { setFoto({ url: image.url, date: image.date || fechaDeUrl(image.url) }); setPts([]); setVista(null); picker.close(); }}
          onClose={picker.close}
        />
      )}
    </div>
  );
};

export default MedirObra;
